import { Controller } from "@hotwired/stimulus"

// Enables submit buttons only when the form has changes from its initial state.
// Empty fields are tracked: clearing a field to empty counts as a change.
export default class extends Controller {
  connect() {
    this.form = this.element.tagName === "FORM" ? this.element : this.element.querySelector("form")
    if (!this.form) return

    this.isInitializing = true
    this.initialValues = {}
    this.fields = []

    // Disable submit buttons immediately (no flash of enabled state)
    this.setSubmitButtonsState(false)

    // Capture after DOM is ready, then again after widgets (datepicker, Tom Select) have settled.
    // We only enable/disable buttons after the final capture so we never briefly enable on load.
    setTimeout(() => {
      this.captureInitialState()
      this.isInitializing = false
      this.settleTimeout = setTimeout(() => {
        this.captureInitialState()
        this.setSubmitButtonsState(this.isDirty())
      }, 1000)
    }, 500)

    this.form.addEventListener("input", this.checkDirty.bind(this))
    this.form.addEventListener("change", this.checkDirty.bind(this))
    document.addEventListener("tom-select:change", this.checkDirty.bind(this))

    document.addEventListener("turbo:frame-load", this.frameLoadHandler.bind(this))
  }

  disconnect() {
    if (this.settleTimeout) clearTimeout(this.settleTimeout)
    if (this.form) {
      this.form.removeEventListener("input", this.checkDirty.bind(this))
      this.form.removeEventListener("change", this.checkDirty.bind(this))
    }
    document.removeEventListener("tom-select:change", this.checkDirty.bind(this))
    document.removeEventListener("turbo:frame-load", this.frameLoadHandler.bind(this))
  }

  frameLoadHandler(event) {
    const frame = event.target
    if (frame.id === "rules" || this.form.contains(frame)) {
      setTimeout(() => this.refreshFields(), 50)
    }
  }

  captureInitialState() {
    this.initialValues = {}
    this.fields = this.getTrackedFields()
    this.fields.forEach((field) => {
      this.initialValues[field.name] = this.getFieldValue(field)
    })
  }

  getTrackedFields() {
    const inputs = this.form.querySelectorAll("input, select, textarea")
    const all = Array.from(inputs).filter((el) => {
      if (!el.name) return false
      if (el.type === "submit" || el.type === "button") return false
      return true
    })
    // Rails check_box generates hidden + checkbox with same name; only track the checkbox
    // so we don't store one value and compare the other (hidden "disabled" vs checkbox true/false).
    const namesWithCheckbox = new Set(
      all.filter((el) => el.type === "checkbox").map((el) => el.name)
    )
    return all.filter((el) => {
      if (el.type === "hidden" && namesWithCheckbox.has(el.name)) return false
      return true
    })
  }

  getFieldValue(field) {
    if (field.type === "checkbox" || field.type === "radio") {
      return field.checked
    }
    return field.value === undefined || field.value === null ? "" : String(field.value)
  }

  isDirty() {
    if (Object.keys(this.initialValues).length === 0) return false
    const fields = this.getTrackedFields()
    return fields.some((field) => {
      if (!this.initialValues.hasOwnProperty(field.name)) return false
      const current = this.getFieldValue(field)
      const initial = this.initialValues[field.name]
      return current !== initial
    })
  }

  checkDirty() {
    if (this.isInitializing) return
    this.setSubmitButtonsState(this.isDirty())
  }

  // Call when new fields may have been added (e.g. turbo frame loaded)
  refreshFields() {
    if (this.isInitializing) return
    const fields = this.getTrackedFields()
    fields.forEach((field) => {
      if (!this.initialValues.hasOwnProperty(field.name)) {
        this.initialValues[field.name] = this.getFieldValue(field)
      }
    })
    this.fields = fields
    this.setSubmitButtonsState(this.isDirty())
  }

  setSubmitButtonsState(enabled) {
    const buttons = this.getSubmitButtons()
    buttons.forEach((btn) => {
      btn.disabled = !enabled
      btn.setAttribute("aria-disabled", !enabled)
    })
  }

  getSubmitButtons() {
    // Always query from the form so we get every submit button in sync
    return this.form ? Array.from(this.form.querySelectorAll('button[type="submit"]')) : []
  }
}
